<template>
  <v-app-bar
    app
    :height="88"
    :extended="extended"
    :extension-height="extendedHeight"
    clipped-left
    class="c360-header"
  >
    <c360-switch-button
      v-if="showExtension"
      :activated="extended"
      class="mr-3"
      @click="toggleExtension"
    />
    <img
      v-if="!extended && !hideLogo"
      :src="logo"
      :style="{
        maxWidth: '170px',
        maxHeight: '48px',
      }"
      alt="Logo"
      data-qa="c360-logo"
      v-ripple
      class="logo"
      @click="clickableLogo && $emit('logo-click')"
    />
    <slot v-if="!extended" name="left" />
    <p v-else class="switch-label mb-0">Switch product:</p>
    <v-spacer />
    <slot name="right" />
    <template #extension v-if="extended">
      <div class="c360-header-drawer" v-resize="onResize">
        <div class="c360-header-drawer-products pt-4 pb-4" ref="productsWrapper">
          <c360-product-link
            v-for="product in productsToDisplay"
            :key="product.id"
            :id="product.id"
            :active="product.id === forProduct"
            :product="product.name"
            :wip="product.wip"
            :disabled="product.disabled"
            :variant="linkVariant"
            :tooltip="product.tooltip"
            class="mr-7 mb-4"
            :class="product.id"
            @click="onProductClick(product.id)"
          />
        </div>
        <c360-portal-button
          :class="linkVariant === 'small' ? 'mb-6' : 'mb-7'"
          :link="returnTo.link"
          :variant="linkVariant"
          @click="onReturnToClick"
        />
      </div>
    </template>
  </v-app-bar>
</template>

<script>
  import Vue from 'vue';
  import { productsSettings, getMyProducts } from '../model';
  import { Product } from '../../../shared/utils/product';
  import { C360Ability } from '../../../shared/abilities';

  import { C360SwitchButton } from '../../SwitchButton';
  import { C360ProductLink } from '../../ProductLink';
  import { C360PortalButton } from '../../PortalButton';
  import { usePortalLink } from '../../../shared/portal-link';

  export default Vue.extend({
    name: 'C360Header',

    inject: ['$c360', '$can', '$tokenProvider', '$accessTokenProvider'],

    components: {
      C360SwitchButton,
      C360ProductLink,
      C360PortalButton,
    },

    props: {
      /**
       * The name of the product the header is currently used for.
       */
      forProduct: {
        type: String,
        required: true,
      },

      /**
       * Whether the logo should be hidden.
       */
      hideLogo: {
        type: Boolean,
        default: false,
      },

      /**
       * Whether the logo should be clickable.
       * If set to true the logo will emit a __`logo-click`__ event on click.
       */
      clickableLogo: {
        type: Boolean,
        default: true,
      },

      /**
       * Provide pre-redirect callback, e.g. for tracking.
       * NOTE: This function must return a Promise, otherwise function call will be interrupted by redirect.
       */
      preRedirectCallback: {
        type: Function,
        default: () => async (product) => product,
      },
    },

    data() {
      return {
        extended: false,
        gqlProductList: [],
        extendedHeight: 0,
      };
    },

    async beforeMount() {
      const token = this.$accessTokenProvider();
      this.gqlProductList = await getMyProducts(token);
    },

    computed: {
      accessTokenProvider() {
        return this.$accessTokenProvider();
      },
      showExtension() {
        if (this.$can(C360Ability.AlwaysSeeSwitchProductDrawer)) {
          return true;
        }
        if (this.productsToDisplay.length === 0) {
          return false;
        }
        const currentProduct = this.$c360.product;
        return !(
          this.productsToDisplay.length === 1 && this.productsToDisplay[0].id === currentProduct
        );
      },
      linkVariant() {
        return this.$vuetify.breakpoint.mdAndDown ? 'small' : 'normal';
      },
      productsToDisplay() {
        return this.gqlProductList
          .map(({ id, enabled, tooltip }) => {
            if (productsSettings.has(id)) {
              const {
                meta: { name },
              } = productsSettings.get(id);

              return {
                name,
                id,
                wip: !enabled,
                disabled: !enabled,
                tooltip,
              };
            }

            return null;
          })
          .filter((item) => item !== null);
      },
      returnTo() {
        const { link } = usePortalLink(this.$c360.portalName);

        return { link };
      },
      logo() {
        return this.$c360.logo;
      },
    },

    watch: {
      extended(value) {
        if (value) {
          this.$nextTick(function () {
            this.resizer();
          });
        }
      },
      linkVariant() {
        this.resizer();
      },
      async accessTokenProvider(token) {
      this.gqlProductList = await getMyProducts(token);
      },
    },

    methods: {
      resizer() {
        this.$nextTick(function () {
          this.extendedHeight = this.$refs?.productsWrapper?.clientHeight;
        });
      },
      onResize() {
        if (this.extended) {
          this.resizer();
        }
      },
      toggleExtension() {
        if (!this.showExtension) return;
        this.extended = !this.extended;
      },
      async onProductClick(product) {
        await Promise.resolve(this.preRedirectCallback(product));
        const token = this.$tokenProvider();
        const hasAnalytics = this.$can(C360Ability.Interact, Product.Analytics);
        productsSettings.get(product)?.redirect(token, { hasAnalytics });
      },
      async onReturnToClick(link) {
        await Promise.resolve(this.preRedirectCallback(Product.Portal));
        const token = this.$tokenProvider();
        window.location.href = `${link}?token=${token}`;
      },
    },
  });
</script>

<style scoped>
  .c360-header-drawer {
    display: flex;
    align-items: center;
    padding: 16px 8px;
    flex-wrap: nowrap;
    flex-direction: row;
    width: 100%;
    align-items: flex-end;
  }

  .c360-header-drawer-products {
    flex-grow: 1;
    display: flex;
    flex-wrap: wrap;
    flex-direction: row;
  }
</style>
